import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { motion } from "framer-motion";
import { STORE_ADDRESS, STORE_PHONE } from "@/lib/brand";
import { ChevronLeft, Mail, Phone, MapPin } from "lucide-react";

/**
 * Shared shell for all legal pages — Privacy, Terms, Returns, Shipping, Accessibility.
 * Editorial layout with a sticky sidebar of section links and honest, human-tone body copy.
 */
export function LegalShell({ title, subtitle, path, updated, sections }) {
  return (
    <div className="min-h-screen bg-ivory">
      <Seo title={title} description={subtitle} path={path} />
      <div className="max-w-[1100px] mx-auto px-5 sm:px-8 pt-16 pb-24">
        <Link to="/" data-testid="legal-back" className="inline-flex items-center gap-1.5 font-label text-[10px] tracking-widest text-ink/60 hover:text-rosemetal mb-8">
          <ChevronLeft className="w-3 h-3" /> BACK TO HEATHER'S
        </Link>

        <motion.header
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="border-b border-cream pb-8 mb-10"
        >
          <p className="font-label text-[10px] tracking-[0.3em] text-rosemetal mb-3">HEATHER'S BOUTIQUE · POLICIES</p>
          <h1 className="font-display text-4xl md:text-5xl leading-tight text-ink">{title}</h1>
          {subtitle && <p className="font-body text-base md:text-lg text-ink/70 mt-4 max-w-2xl leading-relaxed">{subtitle}</p>}
          <p className="font-label text-[9px] tracking-widest text-ink/40 mt-6">LAST UPDATED · {updated}</p>
        </motion.header>

        <div className="grid md:grid-cols-[220px_1fr] gap-10">
          {/* Sticky TOC */}
          <aside className="hidden md:block">
            <div className="sticky top-24 space-y-1.5">
              {sections.map((s) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="block font-label text-[10px] tracking-widest text-ink/55 hover:text-rosemetal py-1 border-l border-cream pl-3 hover:border-rosemetal transition-colors"
                >
                  {s.heading.toUpperCase()}
                </a>
              ))}
            </div>
          </aside>

          {/* Body */}
          <article className="prose max-w-none">
            {sections.map((s) => (
              <section key={s.id} id={s.id} className="mb-10 scroll-mt-24">
                <h2 className="font-display text-2xl md:text-3xl text-ink mb-3">{s.heading}</h2>
                <div className="font-body text-[15px] text-ink/80 leading-[1.75] space-y-3">
                  {s.body}
                </div>
              </section>
            ))}

            {/* Contact block */}
            <section className="mt-14 pt-10 border-t border-cream">
              <p className="font-label text-[10px] tracking-widest text-rosemetal mb-3">QUESTIONS?</p>
              <h3 className="font-display text-2xl text-ink mb-4">Talk to a real human.</h3>
              <ul className="font-body text-sm text-ink/80 space-y-2">
                <li className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-rosemetal" /> <a href={`tel:${STORE_PHONE.replace(/\D/g, "")}`} className="hover:text-rosemetal">{STORE_PHONE}</a></li>
                <li className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-rosemetal" /> <a href="mailto:info@heathersboutique.com" className="hover:text-rosemetal">info@heathersboutique.com</a></li>
                <li className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 text-rosemetal" /> {STORE_ADDRESS}</li>
              </ul>
            </section>
          </article>
        </div>
      </div>
    </div>
  );
}
